import React from 'react';
import { ArrowLeft, Shield, Settings, LogOut } from 'lucide-react';
import { useGuildModeStore } from '../../stores/guildModeStore';
import { enabledGuildModules } from '../views/GuildsView/guildModules';
import { guildLevelProgress } from '../../utils/guildLevel';

interface GuildRailProps {
  isCollapsed: boolean;
}

/**
 * The desktop nav rail while INSIDE a guild (plan §0.3): same island, but
 * the contents become the guild's — crest + name + guild level/XP progress
 * where the user avatar normally sits, the enabled modules as nav items,
 * and a pinned "Back to Metanoia" exit at the top (Discord-home precedent).
 */
export function GuildRail({ isCollapsed }: GuildRailProps) {
  const {
    guild,
    isMember,
    userRole,
    activeModule,
    setModule,
    exitGuildToReturn,
    setShowSettings,
    leaveActiveGuild,
  } = useGuildModeStore();

  const modules = enabledGuildModules(guild);
  const progress = guildLevelProgress(guild?.guild_xp);
  const isLeader = userRole === 'leader' || userRole === 'admin';
  // Leaders (incl. the creator, bootstrapped as leader) get Settings, not
  // Leave — they'd orphan the guild. Finer owner-vs-admin sets are G3.
  const canLeave = isMember && !isLeader;

  return (
    <>
      {/* Pinned exit back to the app */}
      <div className="p-2 border-b border-border">
        <button
          type="button"
          onClick={exitGuildToReturn}
          title={isCollapsed ? 'Back to Metanoia' : undefined}
          className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft className="w-5 h-5 shrink-0" />
          {!isCollapsed && <span className="text-sm truncate">Back to Metanoia</span>}
        </button>
      </div>

      {/* Guild identity — the UserProgress analog */}
      <div className={`flex flex-col items-center border-b border-border ${isCollapsed ? 'py-4 px-2' : 'py-6 px-4'}`}>
        {guild ? (
          <>
            <div
              className={`rounded-xl flex items-center justify-center shadow-md ${isCollapsed ? 'w-10 h-10' : 'w-16 h-16'}`}
              style={{ backgroundColor: guild.crest_color }}
            >
              <Shield className={`text-white ${isCollapsed ? 'w-5 h-5' : 'w-8 h-8'}`} />
            </div>
            {isCollapsed ? (
              <span className="mt-2 text-xs font-medium text-foreground">{progress.level}</span>
            ) : (
              <>
                <p className="mt-3 text-sm font-semibold text-foreground text-center leading-tight line-clamp-2">
                  {guild.name}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Guild Level {progress.level}
                </p>
                <div className="mt-2 w-full h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${Math.min(100, progress.percentage)}%` }}
                  />
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {progress.current} / {progress.required} XP
                </p>
              </>
            )}
          </>
        ) : (
          // still loading the guild row
          <div className={`rounded-xl bg-muted animate-pulse ${isCollapsed ? 'w-10 h-10' : 'w-16 h-16'}`} />
        )}
      </div>

      {/* Guild modules */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {modules.map(module => {
          const Icon = module.icon;
          const active = activeModule === module.id;
          return (
            <button
              key={module.id}
              type="button"
              onClick={() => setModule(module.id)}
              aria-current={active ? 'page' : undefined}
              title={isCollapsed ? module.label : undefined}
              className={`flex items-center gap-3 w-full px-3 py-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-card ${
                active
                  ? 'bg-primary/10 text-primary nav-active-contrast accent-gradient-border'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!isCollapsed && (
                <span className={`text-sm truncate ${active ? 'accent-gradient-text' : ''}`}>
                  {module.label}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Guild-level actions */}
      {(isLeader || canLeave) && (
        <div className="p-2 space-y-1 border-t border-border">
          {isLeader && (
            <button
              type="button"
              onClick={() => setShowSettings(true)}
              title={isCollapsed ? 'Guild Settings' : undefined}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Settings className="w-5 h-5 shrink-0" />
              {!isCollapsed && <span className="text-sm truncate">Guild Settings</span>}
            </button>
          )}
          {canLeave && (
            <button
              type="button"
              onClick={() => leaveActiveGuild()}
              title={isCollapsed ? 'Leave Guild' : undefined}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {!isCollapsed && <span className="text-sm truncate">Leave Guild</span>}
            </button>
          )}
        </div>
      )}
    </>
  );
}
